import React, {
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useState,
} from "react";
import _ from "lodash";
import { useParams, useNavigate, createSearchParams } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import * as quoteReqs from "../../requests/quoteManagementRequests";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import AlertContext from "../../context/alert/AlertContext";
import guid, { GenerateGUID } from "../../modules/guid";
import { Flex } from "reflexbox";
import { Body } from "../UI/Text";
import Table from "../UI/Table";
import Modal from "../UI/Modal";
import Button from "../UI/Button";
import UserContext from "../../context/user/UserContext";
import ProductCatalog from "../ProductCatalog";
import { useQuery } from "../Hooks";
import { getProducts } from "../../requests/productManagementRequests";
import * as constants from "../../modules/constants";
import PagerContextProvider from "../UI/Pager/PagerContext";
import PriceDisplay from "./PriceDisplay";
import { query } from "mssql";

const { createLine, updateLine, deleteLine, copyLineConfiguration, getLines } = quoteReqs;

const ButtonsWrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  transition: 200ms all ease-out;
  height: 100%;
`;

const LineImg = styled.img`
  max-width: 75px;
  max-height: 75px;
  width: auto !important;
  height: auto;
`;

const Lines = ({ refetchQuote, quoteLoading, isLoading, title }) => {
  const { quote, setQuote } = useContext(ConfiguratorContext);
  const { activeUser, pid, isLoading: userLoading } = useContext(UserContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const { showFailureAlert, showSuccessAlert } = useContext(AlertContext);
  const { username } = activeUser || {};
  const configuratorContext = useContext(ConfiguratorContext);
  const { type: quoteType } = quote || {};
  const { quoteId } = useParams();
  const [show, setShow] = useState(false);

  const _lines = useQuery({
    queryID: `lines|${quoteId}`,
    request: quoteReqs.getLines,
    queryVariables: { quoteId, erpId: quote?.customer?.erpId },
    skip: !quoteId || isLoading || quoteLoading || userLoading || quote?.lines,
  });

  useEffect(() => {
    if (
      _lines?.data &&
      !_lines.isLoading &&
      !quote?.lines &&
      !quote?.lineCount &&
      !isLoading
    )
      setQuote({
        ...quote,
        lines: _lines?.data?.rows,
        lineCount: _lines?.data?.count,
      });
  }, [_lines, isLoading, quote, setQuote]);

  const products = useQuery({
    queryID: "quote-products",
    request: getProducts,
    queryVariables: { pid, gid: quote?.groupId },
    skip: !pid || userLoading,
    attributes: [
      "ProductNumber",
      "Name",
      "ID",
      "ApplicationInstance",
      "ApplicationName",
      "isNonConfigurable",
      "isQuickConfig",
      "isNonConfigurable",
      "HeaderId",
      "img",
      "defaultPrice",
      "Description",
      "HeaderId",
      "Overview",
      "quantityInStock",
      "priceCode",
      "Discount",
      "RelatedItem",
    ],
    forcedFilters: {},
  });

  const queryLines = async (quoteId) => {
    const response = await quoteReqs.getLines({
      limit: 0,
      quoteId,
      erpId: quote?.customer?.erpId,
    });
    return response;
  };

  const { setActiveProduct, setIsReconfigure, setConfiguratorMode } =
    configuratorContext;

  const onReconfigure = useCallback(
    async (row) => {
      const { productId, configurationId, id: LineId, headerId } = row;
      setIsReconfigure(true);
      setActiveProduct(productId);
      setConfiguratorMode(constants.CONFIGURATOR_MODES.Default);

      const lineNumber =
        _.findIndex(quote?.lines, (line) => line.id === LineId) + 1;
      refetchQuote("quotes");

      let paramsObj = {
        lineNumber,
        LineId,
        headerId,
        configurationId,
      };

      navigate({
        pathname: `/Configurator/${productId}/${quoteId}/${configurationId}`,
        search: createSearchParams(paramsObj).toString(),
      });
    },
    [
      setIsReconfigure,
      setActiveProduct,
      setConfiguratorMode,
      quote,
      refetchQuote,
      navigate,
      quoteId,
    ]
  );

  const onCopyLine = useCallback(
    async (row) => {
      const oldLine = _.find(quote?.lines, ({ _id }) => _id === row._id);
      const {
        configurationId: sourceConfigurationId,
        headerId: sourceHeaderId,
        price,
        quantity,
        productId,
        isNonConfigurable,
      } = oldLine;

      const newLine = {
        ...oldLine,
        isNonConfigurable,
        // Creating a GUID here to allow for the deleting and updating of rows
        // before the query has refetched.
        _id: guid.GenerateGUID(),
      };
      if (!isNonConfigurable) {
        newLine.sourceConfigurationId = sourceConfigurationId;
        newLine.sourceHeaderId = sourceHeaderId;
        newLine.configurationId = `${quoteId}_${quote?.lines?.length + 1}`;
        newLine.headerId = sourceHeaderId;
      }
      // const newLines = [...quote?.lines, newLine];
      // const newPrice = quote?.totalCost + price * quantity;
      // setQuote({ ...quote, totalCost: newPrice, lines: newLines });

      await copyLineConfiguration({
        sourceHeaderId,
        sourceDetailId: sourceConfigurationId,
        headerId: sourceHeaderId,
        detailId: `${quoteId}_${quote?.lines?.length + 1}`,
        productId,
      });

      // await createLine({
      //   input: { ...newLine },
      // });
      const createRes = await createLine({
        input: { ...newLine },
      });

      if (!createRes?.id) {
        showFailureAlert('Something went wrong. Unable to copy this line.');
        return;
      }

      const newPrice = quote?.totalCost + price * quantity;

      const updatedLines = await queryLines(quoteId);

      _lines.setData({
        rows: updatedLines?.rows,
        count: updatedLines?.count,
      });

      setQuote({
        ...quote,
        totalCost: newPrice,
        lines: updatedLines?.rows,
        lineCount: updatedLines?.count,
      });
    },
    [quote, quoteId, setQuote, showFailureAlert, queryLines, _lines]
  );

  const onDeleteLine = useCallback(
    async (row) => {
      const { lines } = quote || {};
      const { _id, price = 0, quantity = 1 } = row || {};

      const newLines = [...lines];
      const i = _.findIndex(newLines, (line) => line?._id === _id);
      newLines.splice(i, 1);

      const newPrice = quote?.totalCost - +price * +quantity;

      _lines.setData({
        rows: newLines,
        count: _lines?.data?.count - 1,
      });

      setQuote({
        ...quote,
        totalCost: newPrice,
        lines: newLines,
        lineCount: _lines?.data?.count - 1,
      });
      await deleteLine({ _id, quoteId });
      _lines.setData({
        rows: newLines,
        count: _lines?.data?.count - 1,
      });
    },
    [quote, setQuote, quoteId, _lines]
  );

  const onUpdateQuantity = useCallback(
    async (values, row) => {
      const { _id, quantity, price } = row || {};
      const newLines = quote?.lines.map((line) =>
        line._id === _id ? { ...line, quantity: values.quantity } : line
      );
      _lines.setData({
        rows: newLines,
        count: _lines?.data?.count,
      });
      const newPrice =
        quote?.totalCost - quantity * price + values.quantity * price;
      setQuote({
        ...quote,
        totalCost: newPrice,
        lines: newLines,
      });

      await updateLine({
        _id,
        input: { quantity: values.quantity },
      });
    },
    [quote, _lines, setQuote]
  );

  const getSelectedProduct = useCallback(
    (productId) => {
      return _.find(products.data?.rows, (product) => product.ID === productId);
    },
    [products.data]
  );

  const onAddLine = useCallback(
    async (productId, quantity = 1) => {
      setIsReconfigure(false);
      setConfiguratorMode(constants.CONFIGURATOR_MODES.Default);

      const selectedProduct = getSelectedProduct(productId);

      const { lines = [] } = quote || {};
      const { isNonConfigurable, isQuickConfig } = selectedProduct || {};
      if (isNonConfigurable) {
        const index = _.findIndex(
          lines,
          (line) => line.productId === productId
        );

        const newLines = [...lines];
        if (index !== -1) {
          newLines[index].quantity += quantity;
        } else {
          const newLine = {
            _id: GenerateGUID(),
            headerId: selectedProduct?.HeaderId,
            imgUrl:
              selectedProduct?.ImageUrl ||
              selectedProduct?.img ||
              selectedProduct?.base64Img,
            price: selectedProduct?.defaultPrice,
            username: username,
            partNumber: selectedProduct?.ProductNumber,
            description: selectedProduct?.Description,
            productId,
            quoteId,
            quantity,
          };
          newLines.push(newLine);
        }
        const newPrice =
          quote?.totalCost + selectedProduct?.defaultPrice * quantity;
        setQuote({
          ...quote,
          totalCost: newPrice,
          lines: newLines,
          lineCount: newLines.length,
        });
        showSuccessAlert(
          `Added ${selectedProduct?.ProductNumber} to Quote #${quoteId}`
        );
        _lines.setData({
          rows: newLines,
          count: newLines.length,
        });
        if (index !== -1) {
          await updateLine({
            input: { ...newLines[index] },
            _id: newLines[index]._id,
          });
        } else {
          await createLine({
            input: { ...newLines[newLines.length - 1] },
          });
        }
        return;
      } else {
        let lineNumber = 1;

        if (lines && lines.length > 0) {
          lineNumber = lines.length + 1;
        }

        const configurationId = `${quoteId}_${lineNumber}`;
        if (!isNonConfigurable || isQuickConfig)
          navigate({
            pathname: `/Configurator/${productId}/${quoteId}/${configurationId}`,
            search: createSearchParams({
              lineNumber,
            }).toString(),
          });
      }
    },
    [
      setIsReconfigure,
      setConfiguratorMode,
      getSelectedProduct,
      quote,
      username,
      quoteId,
      setQuote,
      _lines,
      showSuccessAlert,
      navigate,
    ]
  );

  const columns = useMemo(
    () => [
      {
        id: "imgUrl",
        data: (row) => {
          return (
            <Flex
              width={1}
              alignItems="center"
              height="100%"
              key={row?.id + "|imgUrl"}
              justifyContent="center"
            >
              {row?.imgUrl && <LineImg src={row?.imgUrl} alt={row?.id || "imgUrl"} />}
            </Flex>
          );
        },
        header: "Preview",
        size: { md: 2, sm: -1, xs: -1 },
      },
      {
        id: "type",
        data: (row) => (
          <Body
            key={row?.type}
            color={row?.id === "new" ? theme.secondary : undefined}
          >
            {row?.type}
          </Body>
        ),
        sort: false,
        header: "Type",
        size: { md: 2, sm: 2, xs: 3 },
      },
      {
        id: "partNumber",
        data: (row) => (
          <Body
            key={row?.partNumber}
            color={row?.id === "new" ? theme.secondary : undefined}
          >
            {row?.partNumber}
          </Body>
        ),
        sort: false,
        header: "Part #",
        size: { md: 2, sm: 2, xs: 3 },
      },
      
      {
        id: "description",
        data: (row) => (
          <Flex
            height="100%"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            key={row?.id + "|description"}
          >
            <Body>{row?.description}</Body>
          </Flex>
        ),
        header: "Description",
        size: { md: 4, sm: 3, xs: -1 },
      },
      
      {
        id: "headerId",
        data: (row) => (
          <Flex
            height="100%"
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            key={row?.id + "|headerId"}
          >
            <Body>{row?.headerId}</Body>
          </Flex>
        ),
        header: "Header ID",
        size: { md: -1, sm: -1, xs: -1 },
      },
      {
        id: "quantity",
        data: (row) => {
          return quoteType === 2 ? (
            <Body center>{row?.quantity}</Body>
          ) : (
            <Flex
              height="100%"
              justifyContent="center"
              alignItems="center"
              key={row?.id + "|quantity"}
            >
              <ButtonsWrapper id="actions" m="0 auto">
                <Button
                  iconName="faMinus"
                  reverse
                  size="tiny"
                  color={theme.secondary}
                  onClick={(e) => {
                    e.preventDefault();
                    return row?.quantity > 1
                      ? onUpdateQuantity({ quantity: row?.quantity - 1 }, row)
                      : showFailureAlert(
                          "Cannot set quantity to a number less than 1."
                        );
                  }}
                />
              </ButtonsWrapper>
              <Body mx="0" bold center>
                {row?.quantity}
              </Body>
              <ButtonsWrapper id="actions" m="0 auto">
                <Button
                  iconName="faPlus"
                  reverse
                  size="tiny"
                  color={theme.secondary}
                  onClick={(e) => {
                    e.preventDefault();
                    onUpdateQuantity({ quantity: row?.quantity + 1 }, row);
                  }}
                />
              </ButtonsWrapper>
            </Flex>
          );
        },
        header: "Quantity",
        size: { md: 2, sm: 2, xs: 5 },
      },
      {
        id: "price",
        data: (row) => <PriceDisplay isUnit row={row} />,

        header: "Unit Price",
        size: { md: 2.5, sm: -1, xs: -1 },
        sort: false,
      },
      {
        id: "totalPrice",
        data: (row) => <PriceDisplay row={row} />,
        header: "Total",
        size: { md: 2.5, sm: 2, xs: 2 },
        sort: false,
      },
      {
        id: "actions",
        header: "",
        sort: false,
        data: (row) => {
          return (
            <ButtonsWrapper
              id="actions"
              m="0 auto"
              height="100%"
              key={row?.id + "|actions"}
            >
              {row?.configurationId && (
                <>
                  <Button
                    size="small"
                    ml="1rem"
                    color={theme.secondary}
                    reverse
                    iconName="faPencil"
                    title="Edit Configuration"
                    onClick={() => onReconfigure(row)}
                  />
                  <Button
                    size="small"
                    color={theme.success}
                    reverse
                    iconName="faCopy"
                    title="Copy Line"
                    onClick={() => onCopyLine(row)}
                  />
                </>
              )}

              <Button
                size="small"
                color={theme.danger}
                reverse
                title="Delete Line"
                iconName="faTrashCan"
                onClick={() => onDeleteLine(row)}
              />
            </ButtonsWrapper>
          );
        },
        size:
          quoteType === 2 && activeUser?.roleLevel < 5
            ? { md: -1, sm: -1, xs: -1 }
            : { lg: 4, md: 0, sm: 0, xs: 0 },
      },
    ],
    [
      onCopyLine,
      onDeleteLine,
      onReconfigure,
      onUpdateQuantity,
      quoteType,
      showFailureAlert,
      theme.danger,
      theme.secondary,
      theme.success,
      activeUser,
    ]
  );

  return (
    <>
      <Modal show={show} setShow={setShow}>
        <PagerContextProvider>
          <ProductCatalog
            onProductClick={onAddLine}
            isLoading={isLoading || products.isLoading}
          />
        </PagerContextProvider>
      </Modal>      
      <PagerContextProvider>
        <Table
          title={title}    
          additionalElements={[
            <ProductCatalog
              key="product-catalog"
              onProductClick={onAddLine}
              inline
            />           
          ]}     
          hideSearch
          hideBack
          hideProperties
          hidePager
          createLink={() => setShow(true)}
          createButtonColor={theme.primary}
          createButtonIcon="faCartPlus"
          createButtonText="Catalog"
          data={quote?.lines || []}
          columns={columns}
          totalCount={quote?.lineCount}
          isLoading={_lines.isLoading}    
          showLinePricingNote={true}      
        />
      </PagerContextProvider>
    </>
  );
};

export default Lines;
